<template>
  <AppLayout pageTitle="My Profile">
    <!-- Profile Update Required Banner -->
    <div v-if="showUpdateBanner" class="row mb-4">
      <div class="col-12">
        <div class="profile-update-banner">
          <div class="banner-icon">
            <i class="ph-duotone ph-warning-circle"></i>
          </div>
          <div class="flex-grow-1">
            <h6 class="fw-bold mb-1" style="color: #92400e;">Please Update Your Profile</h6>
            <p class="mb-0 f-s-13" style="color: #a16207;">
              Your profile has incomplete information. Please update your email address, phone number, and other details to ensure you receive important notifications.
            </p>
          </div>
          <button class="btn btn-sm fw-semibold" @click="startEdit" 
                  style="background: linear-gradient(135deg, #f59e0b, #fbbf24); color: #78350f; border-radius: 10px; padding: 8px 20px; white-space: nowrap;">
            <i class="ph-duotone ph-pencil-simple me-1"></i> Update Now
          </button>
        </div>
      </div>
    </div>

    <!-- Profile Header -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="card border-0 overflow-hidden" style="border-radius: 16px; box-shadow: 0 2px 12px rgba(0,0,0,0.06);">
          <div style="height: 120px; background: linear-gradient(135deg, #0891b2 0%, #06b6d4 50%, #22d3ee 100%);"></div>
          <div class="card-body pt-0 px-4 pb-4">
            <div class="d-flex flex-column flex-md-row align-items-center" style="margin-top: -50px;">
              <div class="rounded-circle d-flex align-items-center justify-content-center border-4 border-white shadow-sm"
                   style="width: 100px; height: 100px; background: linear-gradient(135deg, #0f172a, #334155); font-size: 2rem; color: white; font-weight: 700; flex-shrink: 0;">
                {{ initials }}
              </div>
              <div class="ms-md-4 mt-3 mt-md-4 text-center text-md-start">
                <h4 class="fw-bold mb-1" style="color: #0f172a;">{{ store.userName }}</h4>
                <p class="text-muted mb-0 f-s-14">
                  <i class="ph-duotone ph-identification-card me-1"></i> Member No: {{ store.memberNumber || 'N/A' }}
                </p>
              </div>
              <div class="ms-md-auto mt-3 mt-md-4 d-flex gap-2">
                <button class="btn btn-sm fw-semibold" style="background: linear-gradient(135deg, #0891b2, #06b6d4); color: white; border-radius: 10px; padding: 8px 20px;"
                        @click="startEdit" v-if="!editing">
                  <i class="ph-duotone ph-pencil-simple me-1"></i> Edit Profile
                </button>
                <button class="btn btn-sm btn-outline-danger fw-semibold" style="border-radius: 10px; padding: 8px 20px;"
                        @click="openPasswordModal()">
                  <i class="ph-duotone ph-lock-key me-1"></i> Change Password
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Profile Details -->
    <div class="row">
      <div class="col-lg-8 mb-4">
        <div class="card border-0" style="border-radius: 14px; box-shadow: 0 2px 12px rgba(0,0,0,0.06);">
          <div class="card-header bg-white border-0 pt-4 pb-2 px-4" style="border-radius: 14px 14px 0 0;">
            <h5 class="fw-bold mb-0" style="color: #0f172a;">
              <i class="ph-duotone ph-user-circle me-2 text-primary"></i>
              {{ editing ? 'Edit Details' : 'Personal Information' }}
            </h5>
          </div>
          <div class="card-body px-4 pb-4">
            <!-- Loading -->
            <div v-if="isLoading" class="text-center py-5">
              <div class="spinner-border text-primary" role="status"></div>
              <p class="text-muted mt-2">Loading profile...</p>
            </div>

            <!-- View Mode -->
            <div v-else-if="!editing">
              <div class="row g-4">
                <div class="col-sm-6" v-for="field in viewFields" :key="field.label">
                  <div class="p-3 rounded-3" style="background: #f8fafc;">
                    <small class="text-muted fw-medium f-s-12 text-uppercase" style="letter-spacing: 0.5px;">{{ field.label }}</small>
                    <p class="mb-0 fw-semibold mt-1" style="color: #0f172a;">{{ field.value || '—' }}</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Edit Mode -->
            <div v-else>
              <!-- Edit Form (shown immediately) -->
              <div class="row g-3">
                <div class="col-sm-6">
                  <label class="form-label fw-medium f-s-13">First Name</label>
                  <input type="text" class="form-control" v-model="editForm.first_name" style="border-radius: 10px; padding: 10px 14px;">
                </div>
                <div class="col-sm-6">
                  <label class="form-label fw-medium f-s-13">Last Name</label>
                  <input type="text" class="form-control" v-model="editForm.last_name" style="border-radius: 10px; padding: 10px 14px;">
                </div>
                <div class="col-sm-6">
                  <label class="form-label fw-medium f-s-13">Email Address</label>
                  <input type="email" class="form-control" v-model="editForm.email" style="border-radius: 10px; padding: 10px 14px;">
                </div>
                <div class="col-sm-6">
                  <label class="form-label fw-medium f-s-13">Phone Number</label>
                  <input type="tel" class="form-control" v-model="editForm.mobile_phone" style="border-radius: 10px; padding: 10px 14px;">
                </div>
                <div class="col-sm-6">
                  <label class="form-label fw-medium f-s-13">ID Number</label>
                  <input type="text" class="form-control" v-model="editForm.id_number" style="border-radius: 10px; padding: 10px 14px;">
                </div>
              </div>

              <!-- OTP Verification Section -->
              <div class="mt-4 pt-4 border-top">
                <div class="alert border-0 mb-3" style="background: #ecfeff; color: #0891b2; border-radius: 12px;">
                  <i class="ph-duotone ph-shield-check me-2 f-s-18"></i>
                  <strong>OTP Verification Required to Save</strong> — Request a code sent to your phone to confirm changes.
                </div>

                <div v-if="!otpSent" class="mb-3">
                  <button class="btn fw-semibold" @click="requestOtp" :disabled="otpLoading"
                          style="background: linear-gradient(135deg, #0891b2, #06b6d4); color: white; border-radius: 10px; padding: 10px 24px;">
                    <span v-if="otpLoading" class="spinner-border spinner-border-sm me-2"></span>
                    <i v-else class="ph-duotone ph-chat-dots me-2"></i>
                    Send OTP to Save
                  </button>
                </div>

                <div v-else class="mb-3">
                  <div class="mb-3" style="max-width: 360px;">
                    <label class="form-label fw-medium">Enter OTP Code</label>
                    <div class="d-flex gap-2">
                      <input v-for="(_, i) in 6" :key="i"
                             type="text" maxlength="1"
                             class="form-control text-center fw-bold f-s-20"
                             style="width: 48px; height: 54px; border-radius: 10px; border: 2px solid #e2e8f0;"
                             :ref="'otp' + i"
                             v-model="otpDigits[i]"
                             @input="onOtpInput(i)"
                             @keydown.backspace="onOtpBackspace(i, $event)">
                    </div>
                    <small class="text-muted mt-2 d-block">Enter the 6-digit code sent to your phone</small>
                  </div>
                  <button class="btn btn-sm btn-outline-secondary fw-semibold me-2" @click="requestOtp" :disabled="otpLoading"
                          style="border-radius: 10px; padding: 6px 16px;">
                    <i class="ph-duotone ph-arrow-clockwise me-1"></i> Resend
                  </button>
                </div>
              </div>

              <div class="mt-3 d-flex gap-2">
                <button class="btn fw-semibold" @click="saveProfile" :disabled="saving || otpCode.length < 6"
                        style="background: linear-gradient(135deg, #0891b2, #06b6d4); color: white; border-radius: 10px; padding: 10px 24px;">
                  <span v-if="saving" class="spinner-border spinner-border-sm me-2"></span>
                  <i v-else class="ph-duotone ph-floppy-disk me-2"></i>
                  Save Changes
                </button>
                <button class="btn btn-outline-secondary fw-semibold" @click="cancelEdit" style="border-radius: 10px; padding: 10px 24px;">
                  Cancel
                </button>
              </div>
            </div>

            <!-- Messages -->
            <div v-if="successMsg" class="alert border-0 mt-3" style="background: #ecfdf5; color: #10b981; border-radius: 12px;">
              <i class="ph-bold ph-check-circle me-2"></i> {{ successMsg }}
            </div>
            <div v-if="errorMsg" class="alert border-0 mt-3" style="background: #fef2f2; color: #ef4444; border-radius: 12px;">
              <i class="ph-bold ph-warning-circle me-2"></i> {{ errorMsg }}
            </div>
          </div>
        </div>
      </div>

      <!-- Account Summary Sidebar -->
      <div class="col-lg-4 mb-4">
        <div class="card border-0 mb-4" style="border-radius: 14px; box-shadow: 0 2px 12px rgba(0,0,0,0.06);">
          <div class="card-header bg-white border-0 pt-4 pb-2 px-4" style="border-radius: 14px 14px 0 0;">
            <h6 class="fw-bold mb-0" style="color: #0f172a;">
              <i class="ph-duotone ph-wallet me-2 text-primary"></i> Account Summary
            </h6>
          </div>
          <div class="card-body px-4 pb-4">
            <div v-if="isLoading" class="placeholder-glow">
              <span class="placeholder col-12 rounded mb-3" style="height: 50px;"></span>
              <span class="placeholder col-12 rounded mb-3" style="height: 50px;"></span>
              <span class="placeholder col-12 rounded" style="height: 50px;"></span>
            </div>
            <div v-else>
              <div v-for="account in accounts" :key="account.label" class="d-flex align-items-center justify-content-between py-3 border-bottom">
                <div class="d-flex align-items-center">
                  <div class="rounded-2 d-flex align-items-center justify-content-center me-3" 
                       :style="'width: 36px; height: 36px; background:' + account.bg">
                    <i :class="account.icon" class="text-white f-s-16"></i>
                  </div>
                  <span class="fw-medium f-s-13 text-dark">{{ account.label }}</span>
                </div>
                <span class="fw-bold f-s-14" :class="store.balancesHidden ? 'balance-blurred' : 'balance-visible'" style="color: #0f172a;">{{ account.balance }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Quick Links -->
        <div class="card border-0" style="border-radius: 14px; box-shadow: 0 2px 12px rgba(0,0,0,0.06);">
          <div class="card-header bg-white border-0 pt-4 pb-2 px-4" style="border-radius: 14px 14px 0 0;">
            <h6 class="fw-bold mb-0" style="color: #0f172a;">
              <i class="ph-duotone ph-lightning me-2 text-warning"></i> Quick Links
            </h6>
          </div>
          <div class="card-body px-4 pb-4">
            <router-link to="/transactions" class="d-flex align-items-center text-decoration-none py-2 mb-2 rounded-3 px-3" style="background: #f8fafc;">
              <i class="ph-duotone ph-money me-3 f-s-18 text-success"></i>
              <span class="fw-medium f-s-13 text-dark">Make a Payment</span>
              <i class="ph-bold ph-caret-right ms-auto text-muted f-s-12"></i>
            </router-link>
            <router-link to="/plots" class="d-flex align-items-center text-decoration-none py-2 mb-2 rounded-3 px-3" style="background: #f8fafc;">
              <i class="ph-duotone ph-map-pin-line me-3 f-s-18 text-warning"></i>
              <span class="fw-medium f-s-13 text-dark">View Plots</span>
              <i class="ph-bold ph-caret-right ms-auto text-muted f-s-12"></i>
            </router-link>
            <router-link to="/projects" class="d-flex align-items-center text-decoration-none py-2 rounded-3 px-3" style="background: #f8fafc;">
              <i class="ph-duotone ph-buildings me-3 f-s-18 text-primary"></i>
              <span class="fw-medium f-s-13 text-dark">View Projects</span>
              <i class="ph-bold ph-caret-right ms-auto text-muted f-s-12"></i>
            </router-link>
          </div>
        </div>
      </div>
    </div>

    <!-- Change Password Modal -->
    <div v-if="showPasswordModal" class="modal d-block" tabindex="-1" style="background: rgba(0,0,0,0.5);" @click.self="showPasswordModal = false">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content border-0" style="border-radius: 16px; overflow: hidden;">
          <div class="modal-header border-0 pb-0 px-4 pt-4">
            <h5 class="fw-bold" style="color: #0f172a;">
              <i class="ph-duotone ph-lock-key me-2 text-primary"></i> Change Password
            </h5>
            <button type="button" class="btn-close" @click="showPasswordModal = false"></button>
          </div>
          <div class="modal-body px-4 pb-4">
            <!-- Password OTP Step -->
            <div v-if="!pwOtpVerified">
              <div class="alert border-0 mb-4" style="background: #ecfeff; color: #0891b2; border-radius: 12px;">
                <i class="ph-duotone ph-shield-check me-2 f-s-18"></i>
                <strong>Verification Required</strong> — We'll send an OTP to verify your identity before changing your password.
              </div>

              <div v-if="!pwOtpSent">
                <p class="text-muted mb-3">Click below to receive an OTP on your registered phone number.</p>
                <button class="btn fw-semibold" @click="requestPwOtp" :disabled="otpLoading"
                        style="background: linear-gradient(135deg, #0891b2, #06b6d4); color: white; border-radius: 10px; padding: 10px 24px;">
                  <span v-if="otpLoading" class="spinner-border spinner-border-sm me-2"></span>
                  <i v-else class="ph-duotone ph-chat-dots me-2"></i>
                  Send OTP
                </button>
              </div>

              <div v-else>
                <div class="mb-3" style="max-width: 360px;">
                  <label class="form-label fw-medium">Enter OTP Code</label>
                  <div class="d-flex gap-2">
                    <input v-for="(_, i) in 6" :key="i" 
                           type="text" maxlength="1" 
                           class="form-control text-center fw-bold f-s-20" 
                           style="width: 48px; height: 54px; border-radius: 10px; border: 2px solid #e2e8f0;"
                           :ref="'pwOtp' + i"
                           v-model="pwOtpDigits[i]"
                           @input="onPwOtpInput(i)"
                           @keydown.backspace="onPwOtpBackspace(i, $event)">
                  </div>
                  <small class="text-muted mt-2 d-block">Enter the 6-digit code sent to your phone</small>
                </div>
                <button class="btn fw-semibold me-2" @click="verifyPwOtpLocally" :disabled="pwOtpCode.length < 6"
                        style="background: linear-gradient(135deg, #10b981, #34d399); color: white; border-radius: 10px; padding: 10px 24px;">
                  <i class="ph-duotone ph-check-circle me-2"></i>
                  Continue
                </button>
                <button class="btn btn-outline-secondary fw-semibold" @click="requestPwOtp" :disabled="otpLoading"
                        style="border-radius: 10px; padding: 10px 24px;">
                  Resend
                </button>
              </div>

              <div v-if="pwError" class="alert border-0 mt-3" style="background: #fef2f2; color: #ef4444; border-radius: 12px;">
                <i class="ph-bold ph-warning-circle me-2"></i> {{ pwError }}
              </div>
            </div>

            <!-- Password Form (after OTP verify) -->
            <div v-else>
              <div class="alert border-0 mb-4" style="background: #ecfdf5; color: #10b981; border-radius: 12px;">
                <i class="ph-bold ph-check-circle me-2 f-s-18"></i>
                <strong>Identity Verified</strong> — You can now change your password.
              </div>
            <div class="mb-3">
              <label class="form-label fw-medium f-s-13">Current Password</label>
              <div class="input-group">
                <input :type="showCurrentPw ? 'text' : 'password'" class="form-control" v-model="passwordForm.current_password"
                       style="border-radius: 10px 0 0 10px; padding: 10px 14px;" placeholder="Enter current password">
                <button class="btn btn-outline-secondary" type="button" @click="showCurrentPw = !showCurrentPw" style="border-radius: 0 10px 10px 0;">
                  <i :class="showCurrentPw ? 'ph-bold ph-eye-slash' : 'ph-bold ph-eye'"></i>
                </button>
              </div>
            </div>
            <div class="mb-3">
              <label class="form-label fw-medium f-s-13">New Password</label>
              <div class="input-group">
                <input :type="showNewPw ? 'text' : 'password'" class="form-control" v-model="passwordForm.new_password"
                       style="border-radius: 10px 0 0 10px; padding: 10px 14px;" placeholder="Enter new password">
                <button class="btn btn-outline-secondary" type="button" @click="showNewPw = !showNewPw" style="border-radius: 0 10px 10px 0;">
                  <i :class="showNewPw ? 'ph-bold ph-eye-slash' : 'ph-bold ph-eye'"></i>
                </button>
              </div>
            </div>
            <div class="mb-4">
              <label class="form-label fw-medium f-s-13">Confirm New Password</label>
              <input type="password" class="form-control" v-model="passwordForm.new_password_confirmation"
                     style="border-radius: 10px; padding: 10px 14px;" placeholder="Confirm new password">
            </div>

            <div v-if="pwSuccess" class="alert border-0 mb-3" style="background: #ecfdf5; color: #10b981; border-radius: 12px;">
              <i class="ph-bold ph-check-circle me-2"></i> {{ pwSuccess }}
            </div>
            <div v-if="pwError" class="alert border-0 mb-3" style="background: #fef2f2; color: #ef4444; border-radius: 12px;">
              <i class="ph-bold ph-warning-circle me-2"></i> {{ pwError }}
            </div>

            <button class="btn w-100 fw-semibold" @click="changePassword" :disabled="pwLoading"
                    style="background: linear-gradient(135deg, #0891b2, #06b6d4); color: white; border-radius: 10px; padding: 12px;">
              <span v-if="pwLoading" class="spinner-border spinner-border-sm me-2"></span>
              <i v-else class="ph-duotone ph-lock-key me-2"></i>
              Update Password
            </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </AppLayout>
</template>

<script>
import AppLayout from '@/components/AppLayout.vue';
import api from '@/services/axios.js';
import { useUserStore } from '@/store';

export default {
  name: 'ProfilePage',
  components: { AppLayout },
  data() {
    return {
      profile: null,
      memberData: null,
      isLoading: true,
      editing: false,
      otpSent: false,
      otpVerified: false,
      otpLoading: false,
      otpDigits: ['', '', '', '', '', ''],
      otpPurpose: 'profile_update',
      saving: false,
      editForm: { first_name: '', last_name: '', email: '', mobile_phone: '', id_number: '' },
      successMsg: '',
      errorMsg: '',
      showPasswordModal: false,
      pwOtpSent: false,
      pwOtpVerified: false,
      pwOtpDigits: ['', '', '', '', '', ''],
      pwOtpLoading: false,
      passwordForm: { current_password: '', new_password: '', new_password_confirmation: '' },
      pwLoading: false,
      pwSuccess: '',
      pwError: '',
      showCurrentPw: false,
      showNewPw: false,
      accounts: [],
    };
  },
  computed: {
    store() {
      return useUserStore();
    },
    initials() {
      const name = this.store.userName || '';
      return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
    },
    showUpdateBanner() {
      if (this.editing) return false;
      if (this.$route.query.update === 'required') return true;
      // Also show if profile has incomplete data
      if (!this.memberData) return false;
      const m = this.memberData;
      return !m.email || m.email.includes('temp_email_') || m.email.includes('@example.com') ||
             !m.mobile_phone || !m.id_number;
    },
    otpCode() {
      return this.otpDigits.join('');
    },
    pwOtpCode() {
      return this.pwOtpDigits.join('');
    },
    viewFields() {
      const u = this.profile || {};
      const m = this.memberData || {};
      return [
        { label: 'Full Name', value: u.name || ((m.first_name || '') + ' ' + (m.last_name || '')).trim() || '—' },
        { label: 'Email', value: m.email || u.email || '—' },
        { label: 'Phone', value: m.mobile_phone || u.phone || '—' },
        { label: 'ID Number', value: m.id_number || '—' },
        { label: 'Member Number', value: m.member_number || '—' },
        { label: 'Status', value: m.status || 'Active' },
      ];
    },
  },
  mounted() {
    this.fetchProfile();
  },
  methods: {
    async fetchProfile() {
      try {
        this.isLoading = true;
        this.errorMsg = '';
        const response = await api.get('/api/profile');
        this.profile = response.data.user || null;
        this.memberData = response.data.member || null;

        // Fetch account balances
        const memberId = this.store.memberId;
        if (memberId) {
          try {
            const txRes = await api.get(`/api/members/${memberId}/transactions`);
            const txns = txRes.data.transactions || [];
            const completed = txns.filter(t => t.transaction_status === 'completed');

            const savings = completed.filter(t => t.account_type === 'savings').reduce((s, t) => s + (Number(t.amount) || 0), 0);
            const shares = completed.filter(t => t.account_type === 'share_capital').reduce((s, t) => s + (Number(t.amount) || 0), 0);
            const land = completed.filter(t => t.account_type === 'land_installments').reduce((s, t) => s + (Number(t.amount) || 0), 0);

            this.accounts = [
              { label: 'Deposit Contribution', balance: 'KES ' + savings.toLocaleString(), icon: 'ph-duotone ph-piggy-bank', bg: 'linear-gradient(135deg, #10b981, #34d399)' },
              { label: 'Share Capital', balance: 'KES ' + shares.toLocaleString(), icon: 'ph-duotone ph-coin', bg: 'linear-gradient(135deg, #0891b2, #06b6d4)' },
              { label: 'Plot Investments', balance: 'KES ' + land.toLocaleString(), icon: 'ph-duotone ph-house-line', bg: 'linear-gradient(135deg, #f59e0b, #fbbf24)' },
            ];
          } catch (e) {
            console.error('Failed to load transactions:', e);
          }
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
        // Fallback: use store data from login response
        const storeUser = this.store.user;
        const storeMember = this.store.member;
        if (storeUser || storeMember) {
          this.profile = storeUser ? { id: storeUser.id, name: storeUser.name, email: storeUser.email, phone: storeUser.phone } : null;
          this.memberData = storeMember || null;
          this.errorMsg = '';
        } else {
          this.errorMsg = 'Failed to load profile. Please try again.';
        }
      } finally {
        this.isLoading = false;
      }
    },
    startEdit() {
      this.editing = true;
      this.otpSent = false;
      this.otpVerified = false;
      this.otpDigits = ['', '', '', '', '', ''];
      this.otpPurpose = 'profile_update';
      const m = this.memberData || {};
      const u = this.profile || {};
      const fullName = u.name || '';
      const parts = fullName.split(' ');
      this.editForm = {
        first_name: m.first_name || parts[0] || '',
        last_name: m.last_name || parts.slice(1).join(' ') || '',
        email: m.email || u.email || '',
        mobile_phone: m.mobile_phone || u.phone || '',
        id_number: m.id_number || '',
      };
      this.successMsg = '';
      this.errorMsg = '';
    },
    cancelEdit() {
      this.editing = false;
      this.otpSent = false;
      this.otpVerified = false;
      this.successMsg = '';
      this.errorMsg = '';
    },
    async requestOtp() {
      try {
        this.otpLoading = true;
        this.errorMsg = '';
        await api.post('/api/profile/request-otp', { purpose: 'profile_update' });
        this.otpSent = true;
        this.successMsg = 'OTP sent to your registered phone number.';
      } catch (error) {
        this.errorMsg = error.response?.data?.message || 'Failed to send OTP. Try again.';
      } finally {
        this.otpLoading = false;
      }
    },
    verifyOtpLocally() {
      // Don't verify via API — the backend verifies OTP during update/password-change
      // Just validate locally that we have a 6-digit code
      if (this.otpCode.length === 6) {
        this.otpVerified = true;
        this.successMsg = '';
        this.errorMsg = '';
      } else {
        this.errorMsg = 'Please enter the complete 6-digit OTP code.';
      }
    },
    onOtpInput(index) {
      if (this.otpDigits[index] && index < 5) {
        this.$nextTick(() => {
          const next = this.$refs['otp' + (index + 1)];
          if (next && next[0]) next[0].focus();
        });
      }
    },
    onOtpBackspace(index, event) {
      if (!this.otpDigits[index] && index > 0) {
        event.preventDefault();
        this.otpDigits[index - 1] = '';
        this.$nextTick(() => {
          const prev = this.$refs['otp' + (index - 1)];
          if (prev && prev[0]) prev[0].focus();
        });
      }
    },
    onPwOtpInput(index) {
      if (this.pwOtpDigits[index] && index < 5) {
        this.$nextTick(() => {
          const next = this.$refs['pwOtp' + (index + 1)];
          if (next && next[0]) next[0].focus();
        });
      }
    },
    onPwOtpBackspace(index, event) {
      if (!this.pwOtpDigits[index] && index > 0) {
        event.preventDefault();
        this.pwOtpDigits[index - 1] = '';
        this.$nextTick(() => {
          const prev = this.$refs['pwOtp' + (index - 1)];
          if (prev && prev[0]) prev[0].focus();
        });
      }
    },
    async saveProfile() {
      try {
        this.saving = true;
        this.errorMsg = '';
        const payload = {
          ...this.editForm,
          otp_code: this.otpCode,
        };
        await api.put('/api/profile/update', payload);
        this.successMsg = 'Profile updated successfully!';
        this.editing = false;
        this.otpVerified = false;
        this.fetchProfile();
      } catch (error) {
        this.errorMsg = error.response?.data?.message || 'Failed to update profile.';
      } finally {
        this.saving = false;
      }
    },
    async requestPwOtp() {
      try {
        this.pwOtpLoading = true;
        this.pwError = '';
        await api.post('/api/profile/request-otp', { purpose: 'password_change' });
        this.pwOtpSent = true;
        this.pwSuccess = 'OTP sent to your registered phone number.';
      } catch (error) {
        this.pwError = error.response?.data?.message || 'Failed to send OTP. Try again.';
      } finally {
        this.pwOtpLoading = false;
      }
    },
    verifyPwOtpLocally() {
      if (this.pwOtpCode.length === 6) {
        this.pwOtpVerified = true;
        this.pwSuccess = '';
        this.pwError = '';
      } else {
        this.pwError = 'Please enter the complete 6-digit OTP code.';
      }
    },
    async changePassword() {
      this.pwError = '';
      this.pwSuccess = '';
      if (this.pwOtpCode.length < 6) {
        this.pwError = 'Please enter the 6-digit OTP code.';
        return;
      }
      if (this.passwordForm.new_password !== this.passwordForm.new_password_confirmation) {
        this.pwError = 'Passwords do not match.';
        return;
      }
      if (this.passwordForm.new_password.length < 8) {
        this.pwError = 'Password must be at least 8 characters.';
        return;
      }
      try {
        this.pwLoading = true;
        await api.put('/api/profile/change-password', {
          otp_code: this.pwOtpCode,
          current_password: this.passwordForm.current_password,
          new_password: this.passwordForm.new_password,
          new_password_confirmation: this.passwordForm.new_password_confirmation,
        });
        this.pwSuccess = 'Password changed successfully!';
        this.passwordForm = { current_password: '', new_password: '', new_password_confirmation: '' };
        this.pwOtpDigits = ['', '', '', '', '', ''];
        this.pwOtpSent = false;
        this.pwOtpVerified = false;
        // Clear forced password change flag
        const store = useUserStore();
        store.clearMustChangePassword();
        setTimeout(() => { this.showPasswordModal = false; this.pwSuccess = ''; }, 2000);
      } catch (error) {
        this.pwError = error.response?.data?.message || 'Failed to change password.';
      } finally {
        this.pwLoading = false;
      }
    },
    openPasswordModal() {
      this.showPasswordModal = true;
      this.pwOtpSent = false;
      this.pwOtpVerified = false;
      this.pwOtpDigits = ['', '', '', '', '', ''];
      this.passwordForm = { current_password: '', new_password: '', new_password_confirmation: '' };
      this.pwSuccess = '';
      this.pwError = '';
    },
  },
};
</script>
